import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { generateSummary, getSummary, regenerateSummary } from '../services/api';
import type { SummaryResponse, GenerateSummaryRequest } from '../types';

/**
 * T088 [US3]: Summary hooks — TanStack React Query wrappers.
 *
 * @see contracts/summaries.md
 * @module
 */

// ─── Shopify Toast Helper ────────────────────────────────────

function showToast(message: string, isError = false): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shopify = (globalThis as any).shopify;
  if (typeof shopify?.toast?.show === 'function') {
    shopify.toast.show(message, { isError });
  }
}

function toastError(err: Error): void {
  const msg = err.message || 'Something went wrong';
  showToast(msg, true);
}

// ─── Hooks ───────────────────────────────────────────────────

/** Fetch existing summaries for a product. */
export function useSummary(productId: string) {
  return useQuery<SummaryResponse>({
    queryKey: ['summary', productId],
    queryFn: () => getSummary(productId),
    enabled: !!productId,
    staleTime: 60_000,
    retry: false,
  });
}

/** Generate new AI summaries. Writes the response directly into the cache. */
export function useGenerateSummary() {
  const queryClient = useQueryClient();

  return useMutation<SummaryResponse, Error, GenerateSummaryRequest>({
    mutationFn: (body) => generateSummary(body),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['summary', variables.productId], data);
      showToast('AI summaries generated');
    },
    onError: toastError,
  });
}

/** Regenerate summaries (clears stale flag). Writes the response directly into the cache. */
export function useRegenerateSummary() {
  const queryClient = useQueryClient();

  return useMutation<SummaryResponse, Error, GenerateSummaryRequest>({
    mutationFn: (body) => regenerateSummary(body),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['summary', variables.productId], data);
      showToast('AI summaries regenerated');
    },
    onError: toastError,
  });
}
